export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const ADMIN_API_KEY_STORAGE = "griswold.admin.apiKey";
const ADMIN_SESSION_TOKEN_STORAGE = "griswold.admin.sessionToken";
const API_PREFIX = normalizeApiPrefix(
  import.meta.env.VITE_API_PREFIX || import.meta.env.BASE_URL || "",
);

function normalizeApiPrefix(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || trimmed === "/") return "";
  return `/${trimmed.replace(/^\/+|\/+$/g, "")}`;
}

export function apiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith("/api/")) return `${API_PREFIX}${path}`;
  return path;
}

export function getAdminApiKey(): string {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(ADMIN_API_KEY_STORAGE) || "";
}

export function hasAdminApiKey(): boolean {
  return getAdminApiKey().trim().length > 0;
}

export function getSessionToken(): string {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(ADMIN_SESSION_TOKEN_STORAGE) || "";
}

export function hasSessionToken(): boolean {
  return getSessionToken().trim().length > 0;
}

export function setSessionToken(token: string): void {
  window.localStorage.setItem(ADMIN_SESSION_TOKEN_STORAGE, token);
}

export function clearAdminCredentials(): void {
  window.localStorage.removeItem(ADMIN_SESSION_TOKEN_STORAGE);
  window.localStorage.removeItem(ADMIN_API_KEY_STORAGE);
}

// Per-user staff session tokens take precedence; the shared admin key is the
// break-glass fallback. Backend resolves a Bearer that equals ADMIN_API_KEY to
// the Owner principal, so either path authenticates.
function adminAuthHeaders(): Record<string, string> {
  const token = getSessionToken();
  if (token) return { Authorization: `Bearer ${token}` };
  const key = getAdminApiKey();
  if (key) return { "X-Admin-API-Key": key };
  return {};
}

async function parseError(r: Response): Promise<string> {
  let detail = r.statusText;
  try {
    const j = await r.json();
    if (typeof j === "string") detail = j;
    else if (j && typeof j === "object") {
      if (typeof j.detail === "string") detail = j.detail;
      else if (typeof j.message === "string") detail = j.message;
      else detail = JSON.stringify(j);
    }
  } catch {
    // non-JSON body; keep the statusText
  }
  return detail;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (path.startsWith("/api/v1/admin")) {
    Object.assign(headers, adminAuthHeaders());
  }
  let payload: string | undefined;
  if (body !== undefined) {
    headers["content-type"] = "application/json";
    payload = JSON.stringify(body);
  }
  const r = await fetch(apiUrl(path), { method, headers, body: payload, credentials: "include" });
  if (!r.ok) {
    throw new ApiError(r.status, `${r.status} ${await parseError(r)}`);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

async function uploadMultipart<T>(path: string, form: FormData): Promise<T> {
  // Deliberately do NOT set content-type — the browser supplies the correct
  // multipart boundary. Setting it manually breaks the upload.
  const headers: Record<string, string> = {};
  if (path.startsWith("/api/v1/admin")) {
    Object.assign(headers, adminAuthHeaders());
  }
  const r = await fetch(apiUrl(path), { method: "POST", headers, body: form, credentials: "include" });
  if (!r.ok) {
    throw new ApiError(r.status, `${r.status} ${await parseError(r)}`);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
  upload: <T>(path: string, form: FormData) => uploadMultipart<T>(path, form),
};

export function adminBase(tenantSlug: string): string {
  return `/api/v1/admin/t/${encodeURIComponent(tenantSlug)}`;
}

export function chatBase(tenantSlug: string, chatbotSlug: string): string {
  return `/api/v1/t/${encodeURIComponent(tenantSlug)}/c/${encodeURIComponent(chatbotSlug)}`;
}
